import { authHeader } from './auth';
import type { RuntimeConfig } from './config';

/**
 * Thin HTTP client over fetch().
 *
 * - Every request gets the current bearer token from oidc-client-ts.
 *   If there's no user (or token is expired and cannot be silently
 *   renewed), throw `UnauthorizedError` so the UI can bounce to login.
 *
 * - Non-2xx responses throw `ApiError` carrying the status and any
 *   JSON body the API emitted. Callers can `instanceof` check for
 *   404 / 403 and render appropriate UI.
 *
 * - We do NOT send cookies. The API is configured with `credentials:
 *   false` in CORS, and all auth flows through the Authorization
 *   header. This is intentional — cookies would need a whole CSRF
 *   story we don't want to own.
 */
export class UnauthorizedError extends Error {
  constructor() {
    super('not authenticated');
  }
}

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly body: unknown,
    message: string,
  ) {
    super(message);
  }
}

export interface Learner {
  id: string;
  referenceNo: string;
  givenName: string;
  familyName: string;
  preferredName: string | null;
  dateOfBirth: string;
  email: string | null;
  phone: string | null;
  status: string;
  createdAt: string;
  updatedAt: string;
}

export interface LearnerListResponse {
  data: Learner[];
  page: number;
  pageSize: number;
  total: number;
}

export interface TimelineEvent {
  id: string;
  action: string;
  entityType: string;
  entityId: string | null;
  actorId: string | null;
  metadata: Record<string, unknown>;
  createdAt: string;
}

export interface MeResponse {
  userId: string;
  keycloakId: string;
  email: string;
  name: string;
  roles: string[];
  clientId: string;
  organisation: {
    id: string;
    slug: string;
    name: string;
    type: string;
  };
}

async function request<T>(
  cfg: RuntimeConfig,
  path: string,
  init?: RequestInit,
): Promise<T> {
  const auth = await authHeader();
  if (!auth) throw new UnauthorizedError();

  const url = `${cfg.apiBase}${path}`;
  const res = await fetch(url, {
    ...init,
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      Authorization: auth,
      ...(init?.headers ?? {}),
    },
  });

  if (res.status === 401) {
    throw new UnauthorizedError();
  }

  // 204 has no body; don't try to parse.
  if (res.status === 204) {
    return undefined as T;
  }

  let body: unknown = null;
  const text = await res.text();
  if (text.length > 0) {
    try {
      body = JSON.parse(text);
    } catch {
      body = text;
    }
  }

  if (!res.ok) {
    const msg =
      body && typeof body === 'object' && 'message' in body
        ? String((body as { message: unknown }).message)
        : res.statusText;
    throw new ApiError(res.status, body, msg);
  }

  return body as T;
}

export function getMe(cfg: RuntimeConfig): Promise<MeResponse> {
  return request<MeResponse>(cfg, '/api/v1/me');
}

export function listLearners(
  cfg: RuntimeConfig,
  params: { q?: string; page?: number; pageSize?: number } = {},
): Promise<LearnerListResponse> {
  const qs = new URLSearchParams();
  if (params.q) qs.set('q', params.q);
  if (params.page) qs.set('page', String(params.page));
  if (params.pageSize) qs.set('pageSize', String(params.pageSize));
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return request<LearnerListResponse>(cfg, `/api/v1/learners${suffix}`);
}

export function getLearner(cfg: RuntimeConfig, id: string): Promise<Learner> {
  return request<Learner>(cfg, `/api/v1/learners/${encodeURIComponent(id)}`);
}

export function getLearnerTimeline(
  cfg: RuntimeConfig,
  id: string,
): Promise<TimelineEvent[]> {
  return request<TimelineEvent[]>(
    cfg,
    `/api/v1/learners/${encodeURIComponent(id)}/timeline`,
  );
}

/**
 * Fields the API accepts on POST /learners and PATCH /learners/:id.
 * Mirrors apps/api/src/modules/learners/dto/{create,update}-learner.dto.ts —
 * we keep them aligned by hand because duplicating 8 fields is cheaper
 * than generating types from the OpenAPI doc (for R1 at least).
 *
 * dateOfBirth is sent as a YYYY-MM-DD string because `JSON.stringify` of
 * a Date would turn it into a full ISO timestamp, which the
 * class-validator @IsDate pipe on the API rejects.
 */
export interface LearnerInput {
  referenceNo: string;
  givenName: string;
  familyName: string;
  preferredName?: string | null;
  dateOfBirth: string;
  email?: string | null;
  phone?: string | null;
}

export function createLearner(
  cfg: RuntimeConfig,
  dto: LearnerInput,
): Promise<Learner> {
  return request<Learner>(cfg, '/api/v1/learners', {
    method: 'POST',
    body: JSON.stringify(dto),
  });
}

export function updateLearner(
  cfg: RuntimeConfig,
  id: string,
  dto: Partial<LearnerInput> & { status?: string },
): Promise<Learner> {
  return request<Learner>(cfg, `/api/v1/learners/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify(dto),
  });
}

export function archiveLearner(
  cfg: RuntimeConfig,
  id: string,
): Promise<Learner> {
  return request<Learner>(
    cfg,
    `/api/v1/learners/${encodeURIComponent(id)}/archive`,
    { method: 'POST' },
  );
}

export interface LearnerImportResult {
  /**
   * Synthetic batch id stamped by the API so the audit interceptor
   * can record `learner.batch / <uuid>`. The UI doesn't need it.
   */
  id?: string;
  created: number;
  updated: number;
  skipped: number;
  errors: Array<{ row: number; referenceNo?: string; message: string }>;
}

/**
 * Upload a CSV file of learners.
 *
 * This one doesn't go through the shared `request()` helper because:
 *   1. The body is FormData, not JSON — fetch sets its own Content-Type
 *      with the boundary, and setting it manually breaks multer parsing.
 *   2. We need the Authorization header but NOT the 'Content-Type: application/json'
 *      that request() forces.
 */
// ---------------------------------------------------------------------
// M2 / MIS core types + endpoints
// ---------------------------------------------------------------------
//
// These shapes mirror the API DTOs in apps/api/src/modules/{courses,
// terms,rooms,cohorts,enrolments}. We hand-maintain them rather than
// generating from OpenAPI — duplication is cheap for R1 and the
// TypeScript compiler enforces call sites when we drift.

export interface Course {
  id: string;
  code: string;
  title: string;
  description: string | null;
  subject: string | null;
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CourseInput {
  code: string;
  title: string;
  description?: string | null;
  subject?: string | null;
  active?: boolean;
}

export interface Term {
  id: string;
  name: string;
  academicYear: string;
  startDate: string;
  endDate: string;
  createdAt: string;
  updatedAt: string;
}

export interface TermInput {
  name: string;
  academicYear: string;
  startDate: string; // YYYY-MM-DD
  endDate: string;
}

export interface Room {
  id: string;
  code: string;
  name: string;
  type: string | null;
  capacity: number | null;
  createdAt: string;
  updatedAt: string;
}

export interface RoomInput {
  code: string;
  name: string;
  type?: string | null;
  capacity?: number | null;
}

export interface Cohort {
  id: string;
  courseId: string;
  termId: string;
  code: string;
  title: string | null;
  capacity: number | null;
  status: string;
  createdAt: string;
  updatedAt: string;
}

export interface CohortListRow extends Cohort {
  course: Course;
  term: Term;
  enrolmentCount: number;
}

export interface CohortInput {
  courseId: string;
  termId: string;
  code: string;
  title?: string | null;
  capacity?: number | null;
  status?: string;
}

export interface CohortStaffRow {
  id: string;
  cohortId: string;
  userId: string;
  role: string;
  user: { id: string; name: string; email: string };
}

export interface TimetableSlotRow {
  id: string;
  cohortId: string;
  roomId: string | null;
  dayOfWeek: number;
  startTime: string;
  endTime: string;
  label: string | null;
  room: Room | null;
}

export interface TimetableSlotInput {
  dayOfWeek: number;
  startTime: string;
  endTime: string;
  roomId?: string | null;
  label?: string | null;
}

export interface EnrolmentRow {
  id: string;
  learnerId: string;
  cohortId: string;
  startDate: string;
  endDate: string | null;
  status: string;
  createdAt: string;
  updatedAt: string;
}

export interface CohortRosterEntry extends EnrolmentRow {
  learner: {
    id: string;
    referenceNo: string;
    givenName: string;
    familyName: string;
    preferredName: string | null;
    status: string;
  };
}

export interface CohortDeep {
  cohort: Cohort;
  course: Course;
  term: Term;
  staff: CohortStaffRow[];
  slots: TimetableSlotRow[];
  enrolments: CohortRosterEntry[];
}

export interface LearnerEnrolment extends EnrolmentRow {
  cohort: Cohort & { course: Course; term: Term };
}

export interface EnrolmentInput {
  learnerId: string;
  cohortId: string;
  startDate?: string; // YYYY-MM-DD
}

export interface WithdrawInput {
  outcome: 'withdrawn' | 'completed' | 'transferred';
  endDate?: string;
  reason?: string;
}

// ---- Courses ----

export function listCourses(
  cfg: RuntimeConfig,
  includeInactive = true,
): Promise<Course[]> {
  return request<Course[]>(
    cfg,
    `/api/v1/courses?includeInactive=${includeInactive ? 'true' : 'false'}`,
  );
}

export function createCourse(
  cfg: RuntimeConfig,
  dto: CourseInput,
): Promise<Course> {
  return request<Course>(cfg, '/api/v1/courses', {
    method: 'POST',
    body: JSON.stringify(dto),
  });
}

export function updateCourse(
  cfg: RuntimeConfig,
  id: string,
  dto: Partial<CourseInput>,
): Promise<Course> {
  return request<Course>(cfg, `/api/v1/courses/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify(dto),
  });
}

// ---- Terms ----

export function listTerms(cfg: RuntimeConfig): Promise<Term[]> {
  return request<Term[]>(cfg, '/api/v1/terms');
}

export function createTerm(
  cfg: RuntimeConfig,
  dto: TermInput,
): Promise<Term> {
  return request<Term>(cfg, '/api/v1/terms', {
    method: 'POST',
    body: JSON.stringify(dto),
  });
}

// ---- Rooms ----

export function listRooms(cfg: RuntimeConfig): Promise<Room[]> {
  return request<Room[]>(cfg, '/api/v1/rooms');
}

export function createRoom(
  cfg: RuntimeConfig,
  dto: RoomInput,
): Promise<Room> {
  return request<Room>(cfg, '/api/v1/rooms', {
    method: 'POST',
    body: JSON.stringify(dto),
  });
}

// ---- Cohorts ----

export function listCohorts(cfg: RuntimeConfig): Promise<CohortListRow[]> {
  return request<CohortListRow[]>(cfg, '/api/v1/cohorts');
}

export function getCohortDeep(
  cfg: RuntimeConfig,
  id: string,
): Promise<CohortDeep> {
  return request<CohortDeep>(cfg, `/api/v1/cohorts/${encodeURIComponent(id)}`);
}

export function createCohort(
  cfg: RuntimeConfig,
  dto: CohortInput,
): Promise<Cohort> {
  return request<Cohort>(cfg, '/api/v1/cohorts', {
    method: 'POST',
    body: JSON.stringify(dto),
  });
}

export function addTimetableSlot(
  cfg: RuntimeConfig,
  cohortId: string,
  dto: TimetableSlotInput,
): Promise<TimetableSlotRow> {
  return request<TimetableSlotRow>(
    cfg,
    `/api/v1/cohorts/${encodeURIComponent(cohortId)}/slots`,
    { method: 'POST', body: JSON.stringify(dto) },
  );
}

export function removeTimetableSlot(
  cfg: RuntimeConfig,
  cohortId: string,
  slotId: string,
): Promise<void> {
  return request<void>(
    cfg,
    `/api/v1/cohorts/${encodeURIComponent(cohortId)}/slots/${encodeURIComponent(slotId)}`,
    { method: 'DELETE' },
  );
}

// ---- Enrolments ----

export function enrolLearner(
  cfg: RuntimeConfig,
  dto: EnrolmentInput,
): Promise<EnrolmentRow> {
  return request<EnrolmentRow>(cfg, '/api/v1/enrolments', {
    method: 'POST',
    body: JSON.stringify(dto),
  });
}

export function withdrawEnrolment(
  cfg: RuntimeConfig,
  id: string,
  dto: WithdrawInput,
): Promise<EnrolmentRow> {
  return request<EnrolmentRow>(
    cfg,
    `/api/v1/enrolments/${encodeURIComponent(id)}/withdraw`,
    { method: 'POST', body: JSON.stringify(dto) },
  );
}

export function reactivateEnrolment(
  cfg: RuntimeConfig,
  id: string,
): Promise<EnrolmentRow> {
  return request<EnrolmentRow>(
    cfg,
    `/api/v1/enrolments/${encodeURIComponent(id)}/reactivate`,
    { method: 'POST' },
  );
}

export function listLearnerEnrolments(
  cfg: RuntimeConfig,
  learnerId: string,
): Promise<LearnerEnrolment[]> {
  return request<LearnerEnrolment[]>(
    cfg,
    `/api/v1/learners/${encodeURIComponent(learnerId)}/enrolments`,
  );
}

// ---------------------------------------------------------------------
// Legacy: learner CSV import
// ---------------------------------------------------------------------

// ---------------------------------------------------------------------
// Contacts — MIS-002
// ---------------------------------------------------------------------

export interface Contact {
  id: string;
  learnerId: string;
  relationship: string;
  name: string;
  email: string | null;
  phone: string | null;
  address: string | null;
  parentalResponsibility: boolean;
  preferEmail: boolean;
  preferSms: boolean;
  preferLetter: boolean;
  language: string | null;
  restricted: boolean;
  priority: number;
  createdAt: string;
  updatedAt: string;
}

export interface ContactInput {
  learnerId: string;
  relationship: string;
  name: string;
  email?: string | null;
  phone?: string | null;
  address?: string | null;
  parentalResponsibility?: boolean;
  preferEmail?: boolean;
  preferSms?: boolean;
  preferLetter?: boolean;
  language?: string | null;
  restricted?: boolean;
  priority?: number;
}

export function listLearnerContacts(
  cfg: RuntimeConfig,
  learnerId: string,
): Promise<Contact[]> {
  return request<Contact[]>(
    cfg,
    `/api/v1/learners/${encodeURIComponent(learnerId)}/contacts`,
  );
}

export function createContact(
  cfg: RuntimeConfig,
  dto: ContactInput,
): Promise<Contact> {
  return request<Contact>(cfg, '/api/v1/contacts', {
    method: 'POST',
    body: JSON.stringify(dto),
  });
}

export function updateContact(
  cfg: RuntimeConfig,
  id: string,
  dto: Partial<Omit<ContactInput, 'learnerId'>>,
): Promise<Contact> {
  return request<Contact>(cfg, `/api/v1/contacts/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify(dto),
  });
}

export function deleteContact(cfg: RuntimeConfig, id: string): Promise<void> {
  return request<void>(cfg, `/api/v1/contacts/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  });
}

// ---------------------------------------------------------------------
// Groups — MIS-003
// ---------------------------------------------------------------------

export interface Group {
  id: string;
  code: string;
  name: string;
  type: string;
  academicYear: string | null;
  active: boolean;
  createdAt: string;
  updatedAt: string;
  _count: { members: number };
}

export interface GroupInput {
  code: string;
  name: string;
  type?: string;
  academicYear?: string | null;
}

export interface GroupMemberRow {
  id: string;
  groupId: string;
  learnerId: string;
  startDate: string;
  endDate: string | null;
  learner: {
    id: string;
    referenceNo: string;
    givenName: string;
    familyName: string;
    preferredName: string | null;
    status: string;
  };
}

export function listGroups(cfg: RuntimeConfig): Promise<Group[]> {
  return request<Group[]>(cfg, '/api/v1/groups');
}

export function getGroup(cfg: RuntimeConfig, id: string): Promise<Group> {
  return request<Group>(cfg, `/api/v1/groups/${encodeURIComponent(id)}`);
}

export function createGroup(
  cfg: RuntimeConfig,
  dto: GroupInput,
): Promise<Group> {
  return request<Group>(cfg, '/api/v1/groups', {
    method: 'POST',
    body: JSON.stringify(dto),
  });
}

export function updateGroup(
  cfg: RuntimeConfig,
  id: string,
  dto: Partial<GroupInput> & { active?: boolean },
): Promise<Group> {
  return request<Group>(cfg, `/api/v1/groups/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify(dto),
  });
}

export function listGroupMembers(
  cfg: RuntimeConfig,
  groupId: string,
): Promise<GroupMemberRow[]> {
  return request<GroupMemberRow[]>(
    cfg,
    `/api/v1/groups/${encodeURIComponent(groupId)}/members`,
  );
}

export function addGroupMember(
  cfg: RuntimeConfig,
  groupId: string,
  dto: { learnerId: string; startDate?: string },
): Promise<GroupMemberRow> {
  return request<GroupMemberRow>(
    cfg,
    `/api/v1/groups/${encodeURIComponent(groupId)}/members`,
    { method: 'POST', body: JSON.stringify(dto) },
  );
}

export function removeGroupMember(
  cfg: RuntimeConfig,
  groupId: string,
  learnerId: string,
): Promise<void> {
  return request<void>(
    cfg,
    `/api/v1/groups/${encodeURIComponent(groupId)}/members/${encodeURIComponent(learnerId)}`,
    { method: 'DELETE' },
  );
}

// ---------------------------------------------------------------------
// Staff profiles — MIS-004
// ---------------------------------------------------------------------

export interface StaffProfileRow {
  id: string;
  userId: string;
  jobTitle: string | null;
  department: string | null;
  contractType: string | null;
  hoursPerWeek: number | null;
  startDate: string | null;
  endDate: string | null;
  swcNumber: string | null;
  trnNumber: string | null;
  active: boolean;
  createdAt: string;
  updatedAt: string;
  user: { id: string; name: string; email: string };
}

export interface StaffProfileInput {
  userId: string;
  jobTitle?: string | null;
  department?: string | null;
  contractType?: string | null;
  hoursPerWeek?: number | null;
  startDate?: string | null;
  endDate?: string | null;
  swcNumber?: string | null;
  trnNumber?: string | null;
}

export function listStaff(cfg: RuntimeConfig): Promise<StaffProfileRow[]> {
  return request<StaffProfileRow[]>(cfg, '/api/v1/staff');
}

export function getStaffProfile(
  cfg: RuntimeConfig,
  id: string,
): Promise<StaffProfileRow> {
  return request<StaffProfileRow>(
    cfg,
    `/api/v1/staff/${encodeURIComponent(id)}`,
  );
}

export function createStaffProfile(
  cfg: RuntimeConfig,
  dto: StaffProfileInput,
): Promise<StaffProfileRow> {
  return request<StaffProfileRow>(cfg, '/api/v1/staff', {
    method: 'POST',
    body: JSON.stringify(dto),
  });
}

export function updateStaffProfile(
  cfg: RuntimeConfig,
  id: string,
  dto: Partial<Omit<StaffProfileInput, 'userId'>> & { active?: boolean },
): Promise<StaffProfileRow> {
  return request<StaffProfileRow>(
    cfg,
    `/api/v1/staff/${encodeURIComponent(id)}`,
    { method: 'PATCH', body: JSON.stringify(dto) },
  );
}

// ---------------------------------------------------------------------
// Behaviour events — BEH-001
// ---------------------------------------------------------------------

export interface BehaviourEvent {
  id: string;
  learnerId: string;
  polarity: string;
  type: string;
  severity: number;
  points: number;
  description: string | null;
  location: string | null;
  outcome: string | null;
  followUp: string | null;
  occurredAt: string;
  createdAt: string;
  updatedAt: string;
}

export interface BehaviourEventInput {
  learnerId: string;
  polarity?: string;
  type: string;
  severity?: number;
  points?: number;
  description?: string | null;
  location?: string | null;
  outcome?: string | null;
  followUp?: string | null;
  occurredAt: string;
}

export function listLearnerBehaviour(
  cfg: RuntimeConfig,
  learnerId: string,
): Promise<BehaviourEvent[]> {
  return request<BehaviourEvent[]>(
    cfg,
    `/api/v1/learners/${encodeURIComponent(learnerId)}/behaviour`,
  );
}

export function createBehaviourEvent(
  cfg: RuntimeConfig,
  dto: BehaviourEventInput,
): Promise<BehaviourEvent> {
  return request<BehaviourEvent>(cfg, '/api/v1/behaviour-events', {
    method: 'POST',
    body: JSON.stringify(dto),
  });
}

// ---------------------------------------------------------------------
// Attendance — MIS-005
// ---------------------------------------------------------------------

export interface AttendanceMark {
  id: string;
  learnerId: string;
  cohortId: string;
  sessionDate: string;
  session: string;
  mark: string;
  minutesLate: number | null;
  note: string | null;
  recordedById: string | null;
  createdAt: string;
  updatedAt: string;
  learner?: {
    id: string;
    referenceNo: string;
    givenName: string;
    familyName: string;
    preferredName: string | null;
    status: string;
  };
  cohort?: {
    id: string;
    code: string;
    title: string | null;
  };
}

export interface AttendanceMarkInput {
  learnerId: string;
  cohortId: string;
  sessionDate: string;
  session?: string;
  mark: string;
  minutesLate?: number;
  note?: string;
}

export interface AttendanceSummary {
  total: number;
  present: number;
  absent: number;
  late: number;
  authorised: number;
  unauthorised: number;
  percentage: number;
}

export function upsertAttendanceMark(
  cfg: RuntimeConfig,
  dto: AttendanceMarkInput,
): Promise<AttendanceMark> {
  return request<AttendanceMark>(cfg, '/api/v1/attendance', {
    method: 'POST',
    body: JSON.stringify(dto),
  });
}

export function batchUpsertAttendance(
  cfg: RuntimeConfig,
  marks: AttendanceMarkInput[],
): Promise<{ saved: number }> {
  return request<{ saved: number }>(cfg, '/api/v1/attendance/batch', {
    method: 'POST',
    body: JSON.stringify({ marks }),
  });
}

export function listAttendanceByCohortDate(
  cfg: RuntimeConfig,
  cohortId: string,
  date: string,
): Promise<AttendanceMark[]> {
  return request<AttendanceMark[]>(
    cfg,
    `/api/v1/attendance?cohortId=${encodeURIComponent(cohortId)}&date=${encodeURIComponent(date)}`,
  );
}

export function listLearnerAttendance(
  cfg: RuntimeConfig,
  learnerId: string,
): Promise<AttendanceMark[]> {
  return request<AttendanceMark[]>(
    cfg,
    `/api/v1/learners/${encodeURIComponent(learnerId)}/attendance`,
  );
}

export function getLearnerAttendanceSummary(
  cfg: RuntimeConfig,
  learnerId: string,
): Promise<AttendanceSummary> {
  return request<AttendanceSummary>(
    cfg,
    `/api/v1/learners/${encodeURIComponent(learnerId)}/attendance/summary`,
  );
}

// ---------------------------------------------------------------------
// CRM Pipeline — CRM-001
// ---------------------------------------------------------------------

export interface PipelineRow {
  id: string;
  name: string;
  description: string | null;
  active: boolean;
  createdAt: string;
  updatedAt: string;
  _count: { stages: number };
}

export interface PipelineStageRow {
  id: string;
  pipelineId: string;
  name: string;
  position: number;
  color: string | null;
}

export interface PipelineDeep extends PipelineRow {
  stages: PipelineStageRow[];
}

export interface DealRow {
  id: string;
  learnerId: string;
  pipelineId: string;
  stageId: string;
  value: number | null;
  expectedClose: string | null;
  status: string;
  lostReason: string | null;
  ownerId: string | null;
  createdAt: string;
  updatedAt: string;
  learner: { id: string; referenceNo: string; givenName: string; familyName: string };
  stage: { id: string; name: string; color: string | null };
}

export interface PipelineInput {
  name: string;
  description?: string | null;
}

export interface StageInput {
  name: string;
  position?: number;
  color?: string | null;
}

export interface DealInput {
  learnerId: string;
  pipelineId: string;
  stageId: string;
  value?: number | null;
  expectedClose?: string | null;
  ownerId?: string | null;
}

export function listPipelines(cfg: RuntimeConfig): Promise<PipelineRow[]> {
  return request<PipelineRow[]>(cfg, '/api/v1/pipelines');
}

export function getPipeline(cfg: RuntimeConfig, id: string): Promise<PipelineDeep> {
  return request<PipelineDeep>(cfg, `/api/v1/pipelines/${encodeURIComponent(id)}`);
}

export function createPipeline(cfg: RuntimeConfig, dto: PipelineInput): Promise<PipelineRow> {
  return request<PipelineRow>(cfg, '/api/v1/pipelines', {
    method: 'POST',
    body: JSON.stringify(dto),
  });
}

export function createStage(cfg: RuntimeConfig, pipelineId: string, dto: StageInput): Promise<PipelineStageRow> {
  return request<PipelineStageRow>(
    cfg,
    `/api/v1/pipelines/${encodeURIComponent(pipelineId)}/stages`,
    { method: 'POST', body: JSON.stringify(dto) },
  );
}

export function updateStage(
  cfg: RuntimeConfig,
  stageId: string,
  dto: Partial<StageInput>,
): Promise<PipelineStageRow> {
  return request<PipelineStageRow>(
    cfg,
    `/api/v1/pipeline-stages/${encodeURIComponent(stageId)}`,
    { method: 'PATCH', body: JSON.stringify(dto) },
  );
}

export function deleteStage(cfg: RuntimeConfig, stageId: string): Promise<void> {
  return request<void>(
    cfg,
    `/api/v1/pipeline-stages/${encodeURIComponent(stageId)}`,
    { method: 'DELETE' },
  );
}

export function getDeal(cfg: RuntimeConfig, dealId: string): Promise<DealRow> {
  return request<DealRow>(cfg, `/api/v1/deals/${encodeURIComponent(dealId)}`);
}

export function listDeals(cfg: RuntimeConfig, pipelineId?: string): Promise<DealRow[]> {
  const qs = pipelineId ? `?pipelineId=${encodeURIComponent(pipelineId)}` : '';
  return request<DealRow[]>(cfg, `/api/v1/deals${qs}`);
}

export function createDeal(cfg: RuntimeConfig, dto: DealInput): Promise<DealRow> {
  return request<DealRow>(cfg, '/api/v1/deals', {
    method: 'POST',
    body: JSON.stringify(dto),
  });
}

export function moveDeal(cfg: RuntimeConfig, dealId: string, stageId: string): Promise<DealRow> {
  return request<DealRow>(cfg, `/api/v1/deals/${encodeURIComponent(dealId)}/move`, {
    method: 'PATCH',
    body: JSON.stringify({ stageId }),
  });
}

export function updateDealStatus(
  cfg: RuntimeConfig,
  dealId: string,
  status: string,
  lostReason?: string,
): Promise<DealRow> {
  return request<DealRow>(cfg, `/api/v1/deals/${encodeURIComponent(dealId)}/status`, {
    method: 'PATCH',
    body: JSON.stringify({ status, lostReason }),
  });
}

// ---------------------------------------------------------------------
// CRM Activities — CRM-002
// ---------------------------------------------------------------------

export interface ActivityRow {
  id: string;
  learnerId: string;
  type: string;
  subject: string;
  description: string | null;
  dueAt: string | null;
  completedAt: string | null;
  assigneeId: string | null;
  createdById: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ActivityInput {
  learnerId: string;
  type: string;
  subject: string;
  description?: string | null;
  dueAt?: string | null;
  assigneeId?: string | null;
}

export function listLearnerActivities(cfg: RuntimeConfig, learnerId: string): Promise<ActivityRow[]> {
  return request<ActivityRow[]>(
    cfg,
    `/api/v1/learners/${encodeURIComponent(learnerId)}/activities`,
  );
}

export function listTasks(cfg: RuntimeConfig, assigneeId?: string): Promise<ActivityRow[]> {
  const qs = assigneeId ? `?assigneeId=${encodeURIComponent(assigneeId)}` : '';
  return request<ActivityRow[]>(cfg, `/api/v1/activities/tasks${qs}`);
}

export function createActivity(cfg: RuntimeConfig, dto: ActivityInput): Promise<ActivityRow> {
  return request<ActivityRow>(cfg, '/api/v1/activities', {
    method: 'POST',
    body: JSON.stringify(dto),
  });
}

export function completeActivity(cfg: RuntimeConfig, id: string): Promise<ActivityRow> {
  return request<ActivityRow>(cfg, `/api/v1/activities/${encodeURIComponent(id)}/complete`, {
    method: 'POST',
  });
}

// ---------------------------------------------------------------------
// CRM Comms — CRM-003
// ---------------------------------------------------------------------

export interface CommLogRow {
  id: string;
  learnerId: string;
  channel: string;
  direction: string;
  subject: string | null;
  body: string | null;
  recipient: string | null;
  status: string;
  sentAt: string | null;
  sentById: string | null;
  createdAt: string;
}

export interface CommLogInput {
  learnerId: string;
  channel: string;
  direction?: string;
  subject?: string | null;
  body?: string | null;
  recipient?: string | null;
  status?: string;
  sentAt?: string | null;
}

export function listLearnerComms(cfg: RuntimeConfig, learnerId: string): Promise<CommLogRow[]> {
  return request<CommLogRow[]>(
    cfg,
    `/api/v1/learners/${encodeURIComponent(learnerId)}/comms`,
  );
}

export function listCommLogs(cfg: RuntimeConfig): Promise<CommLogRow[]> {
  return request<CommLogRow[]>(cfg, '/api/v1/comm-logs');
}

export function createCommLog(cfg: RuntimeConfig, dto: CommLogInput): Promise<CommLogRow> {
  return request<CommLogRow>(cfg, '/api/v1/comm-logs', {
    method: 'POST',
    body: JSON.stringify(dto),
  });
}

// ---------------------------------------------------------------------
// Invoicing — FIN-001
// ---------------------------------------------------------------------

export interface InvoiceRow {
  id: string;
  learnerId: string;
  invoiceNo: string;
  status: string;
  issueDate: string | null;
  dueDate: string | null;
  paidDate: string | null;
  totalPence: number;
  note: string | null;
  createdAt: string;
  updatedAt: string;
  learner?: { id: string; givenName: string; familyName: string; referenceNo: string };
  lineItems?: InvoiceLineItemRow[];
}

export interface InvoiceLineItemRow {
  id: string;
  invoiceId: string;
  description: string;
  quantity: number;
  unitPence: number;
  totalPence: number;
  cohortId: string | null;
  createdAt: string;
}

export interface InvoiceInput {
  learnerId: string;
  invoiceNo: string;
  issueDate?: string | null;
  dueDate?: string | null;
  note?: string | null;
}

export interface LineItemInput {
  description: string;
  quantity?: number;
  unitPence: number;
  cohortId?: string | null;
}

export function listInvoices(cfg: RuntimeConfig): Promise<InvoiceRow[]> {
  return request<InvoiceRow[]>(cfg, '/api/v1/invoices');
}

export function getInvoice(cfg: RuntimeConfig, id: string): Promise<InvoiceRow> {
  return request<InvoiceRow>(cfg, `/api/v1/invoices/${encodeURIComponent(id)}`);
}

export function createInvoice(cfg: RuntimeConfig, dto: InvoiceInput): Promise<InvoiceRow> {
  return request<InvoiceRow>(cfg, '/api/v1/invoices', {
    method: 'POST',
    body: JSON.stringify(dto),
  });
}

export function addInvoiceLineItem(cfg: RuntimeConfig, invoiceId: string, dto: LineItemInput): Promise<InvoiceLineItemRow> {
  return request<InvoiceLineItemRow>(
    cfg,
    `/api/v1/invoices/${encodeURIComponent(invoiceId)}/line-items`,
    { method: 'POST', body: JSON.stringify(dto) },
  );
}

export function removeInvoiceLineItem(cfg: RuntimeConfig, invoiceId: string, lineItemId: string): Promise<void> {
  return request<void>(
    cfg,
    `/api/v1/invoices/${encodeURIComponent(invoiceId)}/line-items/${encodeURIComponent(lineItemId)}`,
    { method: 'DELETE' },
  );
}

export function updateInvoiceStatus(cfg: RuntimeConfig, id: string, status: string, paidDate?: string): Promise<InvoiceRow> {
  return request<InvoiceRow>(cfg, `/api/v1/invoices/${encodeURIComponent(id)}/status`, {
    method: 'PATCH',
    body: JSON.stringify({ status, paidDate }),
  });
}

export function listLearnerInvoices(cfg: RuntimeConfig, learnerId: string): Promise<InvoiceRow[]> {
  return request<InvoiceRow[]>(
    cfg,
    `/api/v1/learners/${encodeURIComponent(learnerId)}/invoices`,
  );
}

// ---------------------------------------------------------------------
// Timetable — MIS-006
// ---------------------------------------------------------------------

export interface TimetableSlotFull extends TimetableSlotRow {
  cohort: {
    id: string;
    code: string;
    title: string | null;
    course: { id: string; code: string; title: string };
  };
}

export function listTimetable(cfg: RuntimeConfig): Promise<TimetableSlotFull[]> {
  return request<TimetableSlotFull[]>(cfg, '/api/v1/timetable');
}

export function listTimetableByDay(
  cfg: RuntimeConfig,
  day: number,
): Promise<TimetableSlotFull[]> {
  return request<TimetableSlotFull[]>(cfg, `/api/v1/timetable?day=${day}`);
}

// ---------------------------------------------------------------------
// Legacy: learner CSV import
// ---------------------------------------------------------------------

export async function importLearnersCsv(
  cfg: RuntimeConfig,
  file: File,
): Promise<LearnerImportResult> {
  const auth = await authHeader();
  if (!auth) throw new UnauthorizedError();

  const fd = new FormData();
  fd.append('file', file);

  const res = await fetch(`${cfg.apiBase}/api/v1/learners/import`, {
    method: 'POST',
    headers: { Accept: 'application/json', Authorization: auth },
    body: fd,
  });

  if (res.status === 401) throw new UnauthorizedError();

  let body: unknown = null;
  const text = await res.text();
  if (text.length > 0) {
    try {
      body = JSON.parse(text);
    } catch {
      body = text;
    }
  }

  if (!res.ok) {
    const msg =
      body && typeof body === 'object' && 'message' in body
        ? String((body as { message: unknown }).message)
        : res.statusText;
    throw new ApiError(res.status, body, msg);
  }

  return body as LearnerImportResult;
}

// ---------------------------------------------------------------------
// SEND — Special Educational Needs
// ---------------------------------------------------------------------

export interface SendProfileRow {
  id: string;
  learnerId: string;
  sendStatus: string;
  primaryNeed: string | null;
  secondaryNeed: string | null;
  ehcpStatus: string | null;
  ehcpStartDate: string | null;
  ehcpReviewDate: string | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface SendProvisionRow {
  id: string;
  learnerId: string;
  type: string;
  description: string | null;
  frequency: string | null;
  startDate: string | null;
  endDate: string | null;
  active: boolean;
  createdAt: string;
}

export interface SendProfileInput {
  sendStatus?: string;
  primaryNeed?: string | null;
  secondaryNeed?: string | null;
  ehcpStatus?: string | null;
  ehcpStartDate?: string | null;
  ehcpReviewDate?: string | null;
  notes?: string | null;
}

export interface SendProvisionInput {
  learnerId: string;
  type: string;
  description?: string | null;
  frequency?: string | null;
  startDate?: string | null;
  endDate?: string | null;
}

export function getSendProfile(cfg: RuntimeConfig, learnerId: string): Promise<SendProfileRow> {
  return request<SendProfileRow>(cfg, `/api/v1/learners/${encodeURIComponent(learnerId)}/send`);
}

export function updateSendProfile(cfg: RuntimeConfig, learnerId: string, dto: SendProfileInput): Promise<SendProfileRow> {
  return request<SendProfileRow>(cfg, `/api/v1/learners/${encodeURIComponent(learnerId)}/send`, {
    method: 'PATCH',
    body: JSON.stringify(dto),
  });
}

export function listSendProvisions(cfg: RuntimeConfig, learnerId: string): Promise<SendProvisionRow[]> {
  return request<SendProvisionRow[]>(cfg, `/api/v1/learners/${encodeURIComponent(learnerId)}/send/provisions`);
}

export function createSendProvision(cfg: RuntimeConfig, learnerId: string, dto: Omit<SendProvisionInput, 'learnerId'>): Promise<SendProvisionRow> {
  return request<SendProvisionRow>(cfg, `/api/v1/learners/${encodeURIComponent(learnerId)}/send/provisions`, {
    method: 'POST',
    body: JSON.stringify(dto),
  });
}

export function endSendProvision(cfg: RuntimeConfig, id: string): Promise<SendProvisionRow> {
  return request<SendProvisionRow>(cfg, `/api/v1/send/provisions/${encodeURIComponent(id)}/end`, {
    method: 'POST',
  });
}

// ---------------------------------------------------------------------
// Safeguarding — SAF-001
// ---------------------------------------------------------------------

export interface SafeguardingConcernRow {
  id: string;
  learnerId: string;
  category: string;
  severity: string;
  description: string;
  raisedById: string | null;
  raisedAt: string;
  status: string;
  outcome: string | null;
  restricted: boolean;
  closedAt: string | null;
  createdAt: string;
  updatedAt: string;
  learner?: { id: string; referenceNo: string; givenName: string; familyName: string };
  raisedBy?: { id: string; name: string } | null;
}

export interface SafeguardingConcernInput {
  learnerId: string;
  category: string;
  severity?: string;
  description: string;
}

export function listSafeguardingConcerns(cfg: RuntimeConfig): Promise<SafeguardingConcernRow[]> {
  return request<SafeguardingConcernRow[]>(cfg, '/api/v1/safeguarding');
}

export function listLearnerSafeguardingConcerns(cfg: RuntimeConfig, learnerId: string): Promise<SafeguardingConcernRow[]> {
  return request<SafeguardingConcernRow[]>(cfg, `/api/v1/learners/${encodeURIComponent(learnerId)}/safeguarding`);
}

export function getSafeguardingConcern(cfg: RuntimeConfig, id: string): Promise<SafeguardingConcernRow> {
  return request<SafeguardingConcernRow>(cfg, `/api/v1/safeguarding/${encodeURIComponent(id)}`);
}

export function createSafeguardingConcern(cfg: RuntimeConfig, dto: SafeguardingConcernInput): Promise<SafeguardingConcernRow> {
  return request<SafeguardingConcernRow>(cfg, '/api/v1/safeguarding', {
    method: 'POST',
    body: JSON.stringify(dto),
  });
}

export function updateSafeguardingConcern(cfg: RuntimeConfig, id: string, dto: { status?: string; outcome?: string | null; closedAt?: string | null }): Promise<SafeguardingConcernRow> {
  return request<SafeguardingConcernRow>(cfg, `/api/v1/safeguarding/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify(dto),
  });
}

// ---------------------------------------------------------------------
// Documents — DOC-001
// ---------------------------------------------------------------------

export interface DocumentRow {
  id: string;
  learnerId: string | null;
  category: string;
  title: string;
  fileName: string;
  mimeType: string;
  sizeBytes: number;
  storagePath: string;
  expiresAt: string | null;
  uploadedAt: string;
  learner?: { id: string; referenceNo: string; givenName: string; familyName: string } | null;
}

export interface DocumentInput {
  learnerId?: string | null;
  category: string;
  title: string;
  fileName: string;
  mimeType: string;
  sizeBytes: number;
  storagePath: string;
  expiresAt?: string | null;
}

export function listDocuments(cfg: RuntimeConfig, learnerId?: string): Promise<DocumentRow[]> {
  const qs = learnerId ? `?learnerId=${encodeURIComponent(learnerId)}` : '';
  return request<DocumentRow[]>(cfg, `/api/v1/documents${qs}`);
}

export function listLearnerDocuments(cfg: RuntimeConfig, learnerId: string): Promise<DocumentRow[]> {
  return request<DocumentRow[]>(cfg, `/api/v1/learners/${encodeURIComponent(learnerId)}/documents`);
}

export function getDocument(cfg: RuntimeConfig, id: string): Promise<DocumentRow> {
  return request<DocumentRow>(cfg, `/api/v1/documents/${encodeURIComponent(id)}`);
}

export function createDocument(cfg: RuntimeConfig, dto: DocumentInput): Promise<DocumentRow> {
  return request<DocumentRow>(cfg, '/api/v1/documents', {
    method: 'POST',
    body: JSON.stringify(dto),
  });
}

export function deleteDocument(cfg: RuntimeConfig, id: string): Promise<void> {
  return request<void>(cfg, `/api/v1/documents/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

// ---------------------------------------------------------------------
// Assessments — ASM-001
// ---------------------------------------------------------------------

export interface AssessmentRow {
  id: string;
  learnerId: string;
  cohortId: string | null;
  type: string;
  title: string;
  grade: string | null;
  score: number | null;
  maxScore: number | null;
  predictedGrade: string | null;
  assessedAt: string | null;
  feedback: string | null;
  createdAt: string;
  updatedAt: string;
  learner?: { id: string; referenceNo: string; givenName: string; familyName: string };
}

export interface AssessmentInput {
  learnerId: string;
  cohortId?: string | null;
  type: string;
  title: string;
  grade?: string | null;
  score?: number | null;
  maxScore?: number | null;
  predictedGrade?: string | null;
  assessedAt?: string | null;
  feedback?: string | null;
}

export function listAssessments(cfg: RuntimeConfig, params?: { learnerId?: string; cohortId?: string; type?: string }): Promise<AssessmentRow[]> {
  const qs = new URLSearchParams();
  if (params?.learnerId) qs.set('learnerId', params.learnerId);
  if (params?.cohortId) qs.set('cohortId', params.cohortId);
  if (params?.type) qs.set('type', params.type);
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return request<AssessmentRow[]>(cfg, `/api/v1/assessments${suffix}`);
}

export function listLearnerAssessments(cfg: RuntimeConfig, learnerId: string): Promise<AssessmentRow[]> {
  return request<AssessmentRow[]>(cfg, `/api/v1/learners/${encodeURIComponent(learnerId)}/assessments`);
}

export function getAssessment(cfg: RuntimeConfig, id: string): Promise<AssessmentRow> {
  return request<AssessmentRow>(cfg, `/api/v1/assessments/${encodeURIComponent(id)}`);
}

export function createAssessment(cfg: RuntimeConfig, dto: AssessmentInput): Promise<AssessmentRow> {
  return request<AssessmentRow>(cfg, '/api/v1/assessments', {
    method: 'POST',
    body: JSON.stringify(dto),
  });
}

export function updateAssessment(cfg: RuntimeConfig, id: string, dto: Partial<Omit<AssessmentInput, 'learnerId'>>): Promise<AssessmentRow> {
  return request<AssessmentRow>(cfg, `/api/v1/assessments/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify(dto),
  });
}

// ---------------------------------------------------------------------
// Notifications — NTF-001
// ---------------------------------------------------------------------

export interface NotificationRow {
  id: string;
  userId: string;
  type: string;
  title: string;
  body: string | null;
  linkUrl: string | null;
  read: boolean;
  readAt: string | null;
  createdAt: string;
}

export function listNotifications(cfg: RuntimeConfig, unreadOnly?: boolean): Promise<NotificationRow[]> {
  const qs = unreadOnly ? '?unreadOnly=true' : '';
  return request<NotificationRow[]>(cfg, `/api/v1/notifications${qs}`);
}

export function countUnreadNotifications(cfg: RuntimeConfig): Promise<{ count: number }> {
  return request<{ count: number }>(cfg, '/api/v1/notifications/count');
}

export function markNotificationRead(cfg: RuntimeConfig, id: string): Promise<NotificationRow> {
  return request<NotificationRow>(cfg, `/api/v1/notifications/${encodeURIComponent(id)}/read`, {
    method: 'POST',
  });
}

export function markAllNotificationsRead(cfg: RuntimeConfig): Promise<{ count: number }> {
  return request<{ count: number }>(cfg, '/api/v1/notifications/read-all', {
    method: 'POST',
  });
}

// ---------------------------------------------------------------------
// Org Settings — ORG-001
// ---------------------------------------------------------------------

export interface OrgSettingRow {
  id: string;
  key: string;
  value: string;
  updatedAt: string;
}

export function listOrgSettings(cfg: RuntimeConfig): Promise<OrgSettingRow[]> {
  return request<OrgSettingRow[]>(cfg, '/api/v1/settings');
}

export function getOrgSetting(cfg: RuntimeConfig, key: string): Promise<OrgSettingRow | null> {
  return request<OrgSettingRow | null>(cfg, `/api/v1/settings/${encodeURIComponent(key)}`);
}

export function upsertOrgSetting(cfg: RuntimeConfig, key: string, value: string): Promise<OrgSettingRow> {
  return request<OrgSettingRow>(cfg, `/api/v1/settings/${encodeURIComponent(key)}`, {
    method: 'PUT',
    body: JSON.stringify({ value }),
  });
}

export function deleteOrgSetting(cfg: RuntimeConfig, key: string): Promise<void> {
  return request<void>(cfg, `/api/v1/settings/${encodeURIComponent(key)}`, { method: 'DELETE' });
}

// ---------------------------------------------------------------------
// Audit Events — SEC-002 (read-only)
// ---------------------------------------------------------------------

export interface AuditEventRow {
  id: string;
  action: string;
  entityType: string | null;
  entityId: string | null;
  learnerId: string | null;
  userId: string | null;
  metadata: Record<string, unknown> | null;
  ip: string | null;
  userAgent: string | null;
  createdAt: string;
  user?: { id: string; name: string } | null;
  learner?: { id: string; referenceNo: string; givenName: string; familyName: string } | null;
}

export function listAuditEvents(cfg: RuntimeConfig, params?: { page?: number; pageSize?: number }): Promise<{ data: AuditEventRow[]; total: number }> {
  const qs = new URLSearchParams();
  if (params?.page) qs.set('page', String(params.page));
  if (params?.pageSize) qs.set('pageSize', String(params.pageSize));
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return request<{ data: AuditEventRow[]; total: number }>(cfg, `/api/v1/audit-events${suffix}`);
}

/* ------------------------------------------------------------------ */
/*  Exams                                                              */
/* ------------------------------------------------------------------ */

export interface ExamBoardRow {
  id: string;
  orgId: string;
  name: string;
  code: string;
  active: boolean;
  createdAt: string;
}

export interface ExamEntryRow {
  id: string;
  orgId: string;
  learnerId: string;
  examBoardId: string;
  qualification: string;
  series: string;
  status: string;
  seatNumber: string | null;
  examDate: string | null;
  createdAt: string;
  examBoard?: { id: string; name: string; code: string };
  learner?: { id: string; givenName: string; familyName: string; referenceNo: string };
}

export interface ExamResultRow {
  id: string;
  orgId: string;
  learnerId: string;
  examEntryId: string;
  grade: string;
  rawMark: number | null;
  maxMark: number | null;
  ums: number | null;
  resultDate: string | null;
  createdAt: string;
  examEntry?: { qualification: string; series: string };
  learner?: { id: string; givenName: string; familyName: string };
}

export function listExamBoards(cfg: RuntimeConfig): Promise<ExamBoardRow[]> {
  return request<ExamBoardRow[]>(cfg, '/api/v1/exams/boards');
}

export function createExamBoard(cfg: RuntimeConfig, dto: { name: string; code: string; active?: boolean }): Promise<ExamBoardRow> {
  return request<ExamBoardRow>(cfg, '/api/v1/exams/boards', { method: 'POST', body: JSON.stringify(dto) });
}

export function updateExamBoard(cfg: RuntimeConfig, id: string, dto: Partial<{ name: string; code: string; active: boolean }>): Promise<ExamBoardRow> {
  return request<ExamBoardRow>(cfg, `/api/v1/exams/boards/${id}`, { method: 'PATCH', body: JSON.stringify(dto) });
}

export function listExamEntries(cfg: RuntimeConfig, filters?: { learnerId?: string; examBoardId?: string; series?: string; status?: string }): Promise<ExamEntryRow[]> {
  const qs = new URLSearchParams();
  if (filters?.learnerId) qs.set('learnerId', filters.learnerId);
  if (filters?.examBoardId) qs.set('examBoardId', filters.examBoardId);
  if (filters?.series) qs.set('series', filters.series);
  if (filters?.status) qs.set('status', filters.status);
  const suffix = qs.toString() ? `?${qs}` : '';
  return request<ExamEntryRow[]>(cfg, `/api/v1/exams/entries${suffix}`);
}

export function createExamEntry(cfg: RuntimeConfig, dto: { learnerId: string; examBoardId: string; qualification: string; series: string; status?: string; seatNumber?: string; examDate?: string }): Promise<ExamEntryRow> {
  return request<ExamEntryRow>(cfg, '/api/v1/exams/entries', { method: 'POST', body: JSON.stringify(dto) });
}

export function updateExamEntry(cfg: RuntimeConfig, id: string, dto: Partial<ExamEntryRow>): Promise<ExamEntryRow> {
  return request<ExamEntryRow>(cfg, `/api/v1/exams/entries/${id}`, { method: 'PATCH', body: JSON.stringify(dto) });
}

export function listExamResults(cfg: RuntimeConfig, filters?: { learnerId?: string; examEntryId?: string }): Promise<ExamResultRow[]> {
  const qs = new URLSearchParams();
  if (filters?.learnerId) qs.set('learnerId', filters.learnerId);
  if (filters?.examEntryId) qs.set('examEntryId', filters.examEntryId);
  const suffix = qs.toString() ? `?${qs}` : '';
  return request<ExamResultRow[]>(cfg, `/api/v1/exams/results${suffix}`);
}

export function createExamResult(cfg: RuntimeConfig, dto: { learnerId: string; examEntryId: string; grade: string; rawMark?: number; maxMark?: number; ums?: number; resultDate?: string }): Promise<ExamResultRow> {
  return request<ExamResultRow>(cfg, '/api/v1/exams/results', { method: 'POST', body: JSON.stringify(dto) });
}

export function updateExamResult(cfg: RuntimeConfig, id: string, dto: Partial<ExamResultRow>): Promise<ExamResultRow> {
  return request<ExamResultRow>(cfg, `/api/v1/exams/results/${id}`, { method: 'PATCH', body: JSON.stringify(dto) });
}

/* ------------------------------------------------------------------ */
/*  Progress Tracking                                                  */
/* ------------------------------------------------------------------ */

export interface ProgressSnapshotRow {
  id: string;
  orgId: string;
  learnerId: string;
  cohortId: string | null;
  period: string;
  predictedGrade: string | null;
  currentGrade: string | null;
  targetGrade: string | null;
  onTrack: string | null;
  interventions: string | null;
  notes: string | null;
  snapshotDate: string;
  createdAt: string;
  learner?: { id: string; givenName: string; familyName: string; referenceNo: string };
}

export function listProgressSnapshots(cfg: RuntimeConfig, filters?: { learnerId?: string; cohortId?: string; period?: string }): Promise<ProgressSnapshotRow[]> {
  const qs = new URLSearchParams();
  if (filters?.learnerId) qs.set('learnerId', filters.learnerId);
  if (filters?.cohortId) qs.set('cohortId', filters.cohortId);
  if (filters?.period) qs.set('period', filters.period);
  const suffix = qs.toString() ? `?${qs}` : '';
  return request<ProgressSnapshotRow[]>(cfg, `/api/v1/progress${suffix}`);
}

export function createProgressSnapshot(cfg: RuntimeConfig, dto: { learnerId: string; cohortId?: string; period: string; predictedGrade?: string; currentGrade?: string; targetGrade?: string; onTrack?: string; interventions?: string; notes?: string }): Promise<ProgressSnapshotRow> {
  return request<ProgressSnapshotRow>(cfg, '/api/v1/progress', { method: 'POST', body: JSON.stringify(dto) });
}

export function updateProgressSnapshot(cfg: RuntimeConfig, id: string, dto: Partial<ProgressSnapshotRow>): Promise<ProgressSnapshotRow> {
  return request<ProgressSnapshotRow>(cfg, `/api/v1/progress/${id}`, { method: 'PATCH', body: JSON.stringify(dto) });
}

export function deleteProgressSnapshot(cfg: RuntimeConfig, id: string): Promise<void> {
  return request<void>(cfg, `/api/v1/progress/${id}`, { method: 'DELETE' });
}

export function getProgressTimeline(cfg: RuntimeConfig, learnerId: string): Promise<ProgressSnapshotRow[]> {
  return request<ProgressSnapshotRow[]>(cfg, `/api/v1/progress/learner/${learnerId}/timeline`);
}

/* ------------------------------------------------------------------ */
/*  Funding / Bursary                                                  */
/* ------------------------------------------------------------------ */

export interface FundingStreamRow {
  id: string;
  orgId: string;
  name: string;
  code: string;
  type: string;
  academicYear: string | null;
  active: boolean;
  createdAt: string;
}

export interface LearnerFundingRow {
  id: string;
  orgId: string;
  learnerId: string;
  fundingStreamId: string;
  amountPence: number;
  status: string;
  startDate: string | null;
  endDate: string | null;
  notes: string | null;
  createdAt: string;
  learner?: { id: string; givenName: string; familyName: string; referenceNo: string };
  fundingStream?: { id: string; name: string; code: string; type: string };
}

export function listFundingStreams(cfg: RuntimeConfig): Promise<FundingStreamRow[]> {
  return request<FundingStreamRow[]>(cfg, '/api/v1/funding/streams');
}

export function createFundingStream(cfg: RuntimeConfig, dto: { name: string; code: string; type: string; academicYear?: string; active?: boolean }): Promise<FundingStreamRow> {
  return request<FundingStreamRow>(cfg, '/api/v1/funding/streams', { method: 'POST', body: JSON.stringify(dto) });
}

export function updateFundingStream(cfg: RuntimeConfig, id: string, dto: Partial<FundingStreamRow>): Promise<FundingStreamRow> {
  return request<FundingStreamRow>(cfg, `/api/v1/funding/streams/${id}`, { method: 'PATCH', body: JSON.stringify(dto) });
}

export function listFundingAllocations(cfg: RuntimeConfig, filters?: { learnerId?: string; fundingStreamId?: string; status?: string }): Promise<LearnerFundingRow[]> {
  const qs = new URLSearchParams();
  if (filters?.learnerId) qs.set('learnerId', filters.learnerId);
  if (filters?.fundingStreamId) qs.set('fundingStreamId', filters.fundingStreamId);
  if (filters?.status) qs.set('status', filters.status);
  const suffix = qs.toString() ? `?${qs}` : '';
  return request<LearnerFundingRow[]>(cfg, `/api/v1/funding/allocations${suffix}`);
}

export function createFundingAllocation(cfg: RuntimeConfig, dto: { learnerId: string; fundingStreamId: string; amountPence: number; status?: string; startDate?: string; endDate?: string; notes?: string }): Promise<LearnerFundingRow> {
  return request<LearnerFundingRow>(cfg, '/api/v1/funding/allocations', { method: 'POST', body: JSON.stringify(dto) });
}

export function updateFundingAllocation(cfg: RuntimeConfig, id: string, dto: Partial<LearnerFundingRow>): Promise<LearnerFundingRow> {
  return request<LearnerFundingRow>(cfg, `/api/v1/funding/allocations/${id}`, { method: 'PATCH', body: JSON.stringify(dto) });
}

export function deleteFundingAllocation(cfg: RuntimeConfig, id: string): Promise<void> {
  return request<void>(cfg, `/api/v1/funding/allocations/${id}`, { method: 'DELETE' });
}

/* ------------------------------------------------------------------ */
/*  Employers / Placements                                             */
/* ------------------------------------------------------------------ */

export interface EmployerRow {
  id: string;
  orgId: string;
  name: string;
  companiesHouseNo: string | null;
  contactName: string | null;
  contactEmail: string | null;
  contactPhone: string | null;
  address: string | null;
  active: boolean;
  createdAt: string;
  updatedAt: string;
  _count?: { placements: number };
}

export interface PlacementRow {
  id: string;
  orgId: string;
  learnerId: string;
  employerId: string;
  type: string;
  jobTitle: string | null;
  startDate: string | null;
  endDate: string | null;
  hoursPerWeek: number | null;
  status: string;
  notes: string | null;
  createdAt: string;
  learner?: { id: string; givenName: string; familyName: string; referenceNo: string };
  employer?: { id: string; name: string };
}

export function listEmployers(cfg: RuntimeConfig): Promise<EmployerRow[]> {
  return request<EmployerRow[]>(cfg, '/api/v1/employers');
}

export function getEmployer(cfg: RuntimeConfig, id: string): Promise<EmployerRow & { placements: PlacementRow[] }> {
  return request<EmployerRow & { placements: PlacementRow[] }>(cfg, `/api/v1/employers/${id}`);
}

export function createEmployer(cfg: RuntimeConfig, dto: { name: string; companiesHouseNo?: string; contactName?: string; contactEmail?: string; contactPhone?: string; address?: string; active?: boolean }): Promise<EmployerRow> {
  return request<EmployerRow>(cfg, '/api/v1/employers', { method: 'POST', body: JSON.stringify(dto) });
}

export function updateEmployer(cfg: RuntimeConfig, id: string, dto: Partial<EmployerRow>): Promise<EmployerRow> {
  return request<EmployerRow>(cfg, `/api/v1/employers/${id}`, { method: 'PATCH', body: JSON.stringify(dto) });
}

export function listPlacements(cfg: RuntimeConfig, filters?: { learnerId?: string; employerId?: string; type?: string; status?: string }): Promise<PlacementRow[]> {
  const qs = new URLSearchParams();
  if (filters?.learnerId) qs.set('learnerId', filters.learnerId);
  if (filters?.employerId) qs.set('employerId', filters.employerId);
  if (filters?.type) qs.set('type', filters.type);
  if (filters?.status) qs.set('status', filters.status);
  const suffix = qs.toString() ? `?${qs}` : '';
  return request<PlacementRow[]>(cfg, `/api/v1/employers/placements${suffix}`);
}

export function createPlacement(cfg: RuntimeConfig, dto: { learnerId: string; employerId: string; type: string; jobTitle?: string; startDate?: string; endDate?: string; hoursPerWeek?: number; status?: string; notes?: string }): Promise<PlacementRow> {
  return request<PlacementRow>(cfg, '/api/v1/employers/placements', { method: 'POST', body: JSON.stringify(dto) });
}

export function updatePlacement(cfg: RuntimeConfig, id: string, dto: Partial<PlacementRow>): Promise<PlacementRow> {
  return request<PlacementRow>(cfg, `/api/v1/employers/placements/${id}`, { method: 'PATCH', body: JSON.stringify(dto) });
}

/* ------------------------------------------------------------------ */
/*  Sites (multi-site)                                                 */
/* ------------------------------------------------------------------ */

export interface SiteRow {
  id: string;
  orgId: string;
  name: string;
  code: string;
  address: string | null;
  postcode: string | null;
  phone: string | null;
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

export function listSites(cfg: RuntimeConfig): Promise<SiteRow[]> {
  return request<SiteRow[]>(cfg, '/api/v1/sites');
}

export function getSite(cfg: RuntimeConfig, id: string): Promise<SiteRow> {
  return request<SiteRow>(cfg, `/api/v1/sites/${id}`);
}

export function createSite(cfg: RuntimeConfig, dto: { name: string; code: string; address?: string; postcode?: string; phone?: string; active?: boolean }): Promise<SiteRow> {
  return request<SiteRow>(cfg, '/api/v1/sites', { method: 'POST', body: JSON.stringify(dto) });
}

export function updateSite(cfg: RuntimeConfig, id: string, dto: Partial<SiteRow>): Promise<SiteRow> {
  return request<SiteRow>(cfg, `/api/v1/sites/${id}`, { method: 'PATCH', body: JSON.stringify(dto) });
}

export function deleteSite(cfg: RuntimeConfig, id: string): Promise<void> {
  return request<void>(cfg, `/api/v1/sites/${id}`, { method: 'DELETE' });
}

/* ------------------------------------------------------------------ */
/*  DBS Checks                                                         */
/* ------------------------------------------------------------------ */

export interface DbsCheckRow {
  id: string;
  orgId: string;
  staffId: string | null;
  learnerId: string | null;
  checkType: string;
  certificateNumber: string | null;
  issueDate: string | null;
  updateService: boolean;
  updateServiceId: string | null;
  status: string;
  verifiedById: string | null;
  verifiedAt: string | null;
  expiryDate: string | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
  staff?: { id: string; user: { name: string; email: string } } | null;
  learner?: { id: string; givenName: string; familyName: string; referenceNo: string } | null;
  verifiedBy?: { id: string; name: string } | null;
}

export function listDbsChecks(cfg: RuntimeConfig, filters?: { staffId?: string; learnerId?: string; status?: string }): Promise<DbsCheckRow[]> {
  const qs = new URLSearchParams();
  if (filters?.staffId) qs.set('staffId', filters.staffId);
  if (filters?.learnerId) qs.set('learnerId', filters.learnerId);
  if (filters?.status) qs.set('status', filters.status);
  const suffix = qs.toString() ? `?${qs}` : '';
  return request<DbsCheckRow[]>(cfg, `/api/v1/dbs-checks${suffix}`);
}

export function createDbsCheck(cfg: RuntimeConfig, dto: { staffId?: string; learnerId?: string; checkType?: string; certificateNumber?: string; issueDate?: string; updateService?: boolean; updateServiceId?: string; status?: string; expiryDate?: string; notes?: string }): Promise<DbsCheckRow> {
  return request<DbsCheckRow>(cfg, '/api/v1/dbs-checks', { method: 'POST', body: JSON.stringify(dto) });
}

export function updateDbsCheck(cfg: RuntimeConfig, id: string, dto: Partial<DbsCheckRow>): Promise<DbsCheckRow> {
  return request<DbsCheckRow>(cfg, `/api/v1/dbs-checks/${id}`, { method: 'PATCH', body: JSON.stringify(dto) });
}

export function deleteDbsCheck(cfg: RuntimeConfig, id: string): Promise<void> {
  return request<void>(cfg, `/api/v1/dbs-checks/${id}`, { method: 'DELETE' });
}

export function listExpiringDbsChecks(cfg: RuntimeConfig, days?: number): Promise<DbsCheckRow[]> {
  const qs = days ? `?days=${days}` : '';
  return request<DbsCheckRow[]>(cfg, `/api/v1/dbs-checks/expiring${qs}`);
}

/* ------------------------------------------------------------------ */
/*  Reports                                                            */
/* ------------------------------------------------------------------ */

export interface AttendanceSummary {
  total: number;
  present: number;
  absent: number;
  authorised: number;
  attendanceRate: number;
  breakdown: { mark: string; count: number }[];
}

export interface AchievementSummary {
  total: number;
  completed: number;
  withdrawn: number;
  active: number;
  achievementRate: number;
  retentionRate: number;
  breakdown: { status: string; count: number }[];
}

export interface KpiStats {
  totalLearners: number;
  activeEnrolments: number;
  totalStaff: number;
  totalCohorts: number;
}

export interface FinancialSummary {
  invoices: { status: string; count: number; totalPence: number }[];
  funding: { status: string; count: number; totalPence: number }[];
}

export function getAttendanceReport(cfg: RuntimeConfig, filters?: { cohortId?: string; from?: string; to?: string }): Promise<AttendanceSummary> {
  const qs = new URLSearchParams();
  if (filters?.cohortId) qs.set('cohortId', filters.cohortId);
  if (filters?.from) qs.set('from', filters.from);
  if (filters?.to) qs.set('to', filters.to);
  const suffix = qs.toString() ? `?${qs}` : '';
  return request<AttendanceSummary>(cfg, `/api/v1/reports/attendance${suffix}`);
}

export function getAchievementReport(cfg: RuntimeConfig, filters?: { cohortId?: string }): Promise<AchievementSummary> {
  const qs = new URLSearchParams();
  if (filters?.cohortId) qs.set('cohortId', filters.cohortId);
  const suffix = qs.toString() ? `?${qs}` : '';
  return request<AchievementSummary>(cfg, `/api/v1/reports/achievement${suffix}`);
}

export function getKpiReport(cfg: RuntimeConfig): Promise<KpiStats> {
  return request<KpiStats>(cfg, '/api/v1/reports/kpi');
}

export function getFinancialReport(cfg: RuntimeConfig): Promise<FinancialSummary> {
  return request<FinancialSummary>(cfg, '/api/v1/reports/financial');
}

/* ------------------------------------------------------------------ */
/*  Webhooks                                                           */
/* ------------------------------------------------------------------ */

export interface WebhookRow {
  id: string;
  orgId: string;
  name: string;
  url: string;
  secret: string | null;
  events: string[];
  active: boolean;
  lastTriggeredAt: string | null;
  lastStatus: number | null;
  failureCount: number;
  createdAt: string;
  updatedAt: string;
}

export function listWebhooks(cfg: RuntimeConfig): Promise<WebhookRow[]> {
  return request<WebhookRow[]>(cfg, '/api/v1/webhooks');
}

export function createWebhook(cfg: RuntimeConfig, dto: { name: string; url: string; secret?: string; events?: string[]; active?: boolean }): Promise<WebhookRow> {
  return request<WebhookRow>(cfg, '/api/v1/webhooks', { method: 'POST', body: JSON.stringify(dto) });
}

export function updateWebhook(cfg: RuntimeConfig, id: string, dto: Partial<WebhookRow>): Promise<WebhookRow> {
  return request<WebhookRow>(cfg, `/api/v1/webhooks/${id}`, { method: 'PATCH', body: JSON.stringify(dto) });
}

export function deleteWebhook(cfg: RuntimeConfig, id: string): Promise<void> {
  return request<void>(cfg, `/api/v1/webhooks/${id}`, { method: 'DELETE' });
}

export function testWebhook(cfg: RuntimeConfig, id: string): Promise<{ success: boolean; status: number; error?: string }> {
  return request<{ success: boolean; status: number; error?: string }>(cfg, `/api/v1/webhooks/${id}/test`, { method: 'POST' });
}

/* ------------------------------------------------------------------ */
/*  Payments                                                           */
/* ------------------------------------------------------------------ */

export interface PaymentRow {
  id: string;
  orgId: string;
  invoiceId: string;
  amountPence: number;
  method: string;
  reference: string | null;
  gatewayId: string | null;
  gatewayStatus: string | null;
  status: string;
  paidAt: string | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
  invoice?: { id: string; referenceNo: string; totalPence: number };
}

export interface PaymentSummary {
  status: string;
  count: number;
  totalPence: number;
}

export function listPayments(cfg: RuntimeConfig, filters?: { invoiceId?: string; status?: string; method?: string }): Promise<PaymentRow[]> {
  const qs = new URLSearchParams();
  if (filters?.invoiceId) qs.set('invoiceId', filters.invoiceId);
  if (filters?.status) qs.set('status', filters.status);
  if (filters?.method) qs.set('method', filters.method);
  const suffix = qs.toString() ? `?${qs}` : '';
  return request<PaymentRow[]>(cfg, `/api/v1/payments${suffix}`);
}

export function createPayment(cfg: RuntimeConfig, dto: { invoiceId: string; amountPence: number; method?: string; reference?: string; notes?: string }): Promise<PaymentRow> {
  return request<PaymentRow>(cfg, '/api/v1/payments', { method: 'POST', body: JSON.stringify(dto) });
}

export function updatePayment(cfg: RuntimeConfig, id: string, dto: Partial<PaymentRow>): Promise<PaymentRow> {
  return request<PaymentRow>(cfg, `/api/v1/payments/${id}`, { method: 'PATCH', body: JSON.stringify(dto) });
}

export function getPaymentSummary(cfg: RuntimeConfig): Promise<PaymentSummary[]> {
  return request<PaymentSummary[]>(cfg, '/api/v1/payments/summary');
}

/* ------------------------------------------------------------------ */
/*  Portal Tokens                                                      */
/* ------------------------------------------------------------------ */

export interface PortalTokenRow {
  id: string;
  orgId: string;
  type: string;
  learnerId: string;
  email: string;
  name: string;
  expiresAt: string;
  lastUsedAt: string | null;
  active: boolean;
  createdAt: string;
  token?: string;
  learner?: { id: string; givenName: string; familyName: string; referenceNo: string };
}

export function listPortalTokens(cfg: RuntimeConfig, filters?: { type?: string; learnerId?: string }): Promise<PortalTokenRow[]> {
  const qs = new URLSearchParams();
  if (filters?.type) qs.set('type', filters.type);
  if (filters?.learnerId) qs.set('learnerId', filters.learnerId);
  const suffix = qs.toString() ? `?${qs}` : '';
  return request<PortalTokenRow[]>(cfg, `/api/v1/portal/tokens${suffix}`);
}

export function createPortalToken(cfg: RuntimeConfig, dto: { type: string; learnerId: string; email: string; name: string; expiresInDays?: number }): Promise<PortalTokenRow> {
  return request<PortalTokenRow>(cfg, '/api/v1/portal/tokens', { method: 'POST', body: JSON.stringify(dto) });
}

export function revokePortalToken(cfg: RuntimeConfig, id: string): Promise<PortalTokenRow> {
  return request<PortalTokenRow>(cfg, `/api/v1/portal/tokens/${id}`, { method: 'DELETE' });
}

/* ------------------------------------------------------------------ */
/*  Apprenticeship Standards                                           */
/* ------------------------------------------------------------------ */

export interface ApprenticeshipStandardRow {
  id: string;
  orgId: string;
  name: string;
  reference: string | null;
  level: number;
  sector: string | null;
  durationMonths: number;
  maxFunding: number | null;
  epaOrg: string | null;
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

export function listApprenticeshipStandards(cfg: RuntimeConfig): Promise<ApprenticeshipStandardRow[]> {
  return request<ApprenticeshipStandardRow[]>(cfg, '/api/v1/apprenticeships');
}

export function createApprenticeshipStandard(cfg: RuntimeConfig, dto: { name: string; reference?: string; level?: number; sector?: string; durationMonths?: number; maxFunding?: number; epaOrg?: string }): Promise<ApprenticeshipStandardRow> {
  return request<ApprenticeshipStandardRow>(cfg, '/api/v1/apprenticeships', { method: 'POST', body: JSON.stringify(dto) });
}

export function updateApprenticeshipStandard(cfg: RuntimeConfig, id: string, dto: Partial<ApprenticeshipStandardRow>): Promise<ApprenticeshipStandardRow> {
  return request<ApprenticeshipStandardRow>(cfg, `/api/v1/apprenticeships/${id}`, { method: 'PATCH', body: JSON.stringify(dto) });
}

/* ------------------------------------------------------------------ */
/*  Qualifications                                                     */
/* ------------------------------------------------------------------ */

export interface QualificationRow {
  id: string;
  orgId: string;
  title: string;
  framework: string;
  level: string;
  awardingBody: string | null;
  glh: number | null;
  creditValue: number | null;
  sector: string | null;
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

export function listQualifications(cfg: RuntimeConfig, framework?: string): Promise<QualificationRow[]> {
  const suffix = framework ? `?framework=${encodeURIComponent(framework)}` : '';
  return request<QualificationRow[]>(cfg, `/api/v1/qualifications${suffix}`);
}

export function createQualification(cfg: RuntimeConfig, dto: { title: string; framework?: string; level: string; awardingBody?: string; glh?: number; creditValue?: number; sector?: string }): Promise<QualificationRow> {
  return request<QualificationRow>(cfg, '/api/v1/qualifications', { method: 'POST', body: JSON.stringify(dto) });
}

export function updateQualification(cfg: RuntimeConfig, id: string, dto: Partial<QualificationRow>): Promise<QualificationRow> {
  return request<QualificationRow>(cfg, `/api/v1/qualifications/${id}`, { method: 'PATCH', body: JSON.stringify(dto) });
}

/* ------------------------------------------------------------------ */
/*  API Keys                                                           */
/* ------------------------------------------------------------------ */

export interface ApiKeyRow {
  id: string;
  orgId: string;
  name: string;
  prefix: string;
  scopes: string[];
  lastUsedAt: string | null;
  expiresAt: string | null;
  active: boolean;
  createdAt: string;
  key?: string;
}

export function listApiKeys(cfg: RuntimeConfig): Promise<ApiKeyRow[]> {
  return request<ApiKeyRow[]>(cfg, '/api/v1/api-keys');
}

export function createApiKey(cfg: RuntimeConfig, dto: { name: string; scopes?: string[]; expiresInDays?: number }): Promise<ApiKeyRow> {
  return request<ApiKeyRow>(cfg, '/api/v1/api-keys', { method: 'POST', body: JSON.stringify(dto) });
}

export function revokeApiKey(cfg: RuntimeConfig, id: string): Promise<void> {
  return request<void>(cfg, `/api/v1/api-keys/${id}`, { method: 'DELETE' });
}
